import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, ArrowLeft, Mail, ShieldCheck, Fingerprint, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { loginWithPasskey, isPasskeySupported } from '@/lib/passkey';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Saved user from previous login
  const savedUser = JSON.parse(localStorage.getItem('inception_last_user') || 'null');
  const [useSavedUser, setUseSavedUser] = useState(!!savedUser);
  const [email, setEmail] = useState(savedUser?.email || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showFixtures, setShowFixtures] = useState(false);
  const [fixtures, setFixtures] = useState({ round: null, matches: [] });

  // 2FA state
  const [loginToken, setLoginToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      toast.error(t('session_expired'));
    }
    if (searchParams.get('kicked') === '1') {
      toast.error('Your session was terminated by an administrator.');
    }
    const googleError = searchParams.get('error');
    if (googleError) {
      toast.error(googleError);
    }
    // Restore saved email
    const savedEmail = localStorage.getItem('inception_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(p => p - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      const result = await login(email, password);
      // Save or clear remembered email
      if (rememberMe) {
        localStorage.setItem('inception_remembered_email', email);
      } else {
        localStorage.removeItem('inception_remembered_email');
      }
      if (result?.requires_2fa) {
        setLoginToken(result.login_token);
        setMaskedEmail(result.masked_email);
        setCountdown(result.expires_in || 300);
        setCodeDigits(['', '', '', '', '', '']);
        setMode('verify');
        toast.success('Verification code sent to your email');
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        // Save user for next login
        if (result?.user) {
          localStorage.setItem('inception_last_user', JSON.stringify({
            name: result.user.name,
            email: result.user.email,
            avatar_url: result.user.avatar_url || null,
          }));
        }
        toast.success(t('welcome_back'));
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...codeDigits];
    if (value.length > 1) {
      const chars = value.slice(0, 6 - index).split('');
      chars.forEach((ch, i) => { if (index + i < 6) newDigits[index + i] = ch; });
      setCodeDigits(newDigits);
      const nextIdx = Math.min(index + chars.length, 5);
      inputRefs.current[nextIdx]?.focus();
      if (newDigits.every(d => d !== '')) handleVerify(newDigits.join(''));
      return;
    }
    newDigits[index] = value;
    setCodeDigits(newDigits);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (value && newDigits.every(d => d !== '')) handleVerify(newDigits.join(''));
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (codeStr) => {
    const code = codeStr || codeDigits.join('');
    if (code.length !== 6) { toast.error('Please enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/verify-code', { login_token: loginToken, code });
      const { token, user } = res.data;
      loginWithToken(token, user);
      toast.success(t('welcome_back'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
      setCodeDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post('/api/auth/resend-code', { login_token: loginToken });
      setLoginToken(res.data.login_token);
      setMaskedEmail(res.data.masked_email);
      setCountdown(res.data.expires_in || 300);
      setCodeDigits(['', '', '', '', '', '']);
      toast.success('New code sent to your email');
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
      if (err.response?.status === 401) setMode('login');
    } finally { setResending(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
      setMode('sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally { setLoading(false); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const result = await loginWithPasskey();
      loginWithToken(result.token, result.user);
      toast.success(t('welcome_back'));
      navigate('/dashboard');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        // User cancelled the passkey prompt
        return;
      }
      toast.error(err.message || 'Passkey login failed');
    } finally { setPasskeyLoading(false); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-end" data-testid="login-page">
      {/* Full-screen background */}
      <div className="absolute inset-0 z-0">
        <img
          src="/stadium-bg.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Glass login card — right side */}
      <div className="relative z-10 w-full max-w-md mx-6 sm:mx-10 lg:mx-20 my-8">
 <div className="backdrop-blur-xl bg-white/10 border-white/20 rounded-card shadow-card-2xl p-8 sm:p-10 animate-fade-in">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/inception-logo-white.webp"
              alt="Inception"
              className="h-10 object-contain"
              data-testid="login-logo"
            />
          </div>

          {mode === 'login' && (
            <>
              <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form" autoComplete="on">
                {/* Saved user greeting */}
                {useSavedUser && savedUser ? (
                  <>
                    <div className="flex flex-col items-center gap-3 pb-2">
                      <div className="w-14 h-14 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-xl font-bold text-white">
                        {savedUser.avatar_url ? (
                          <img src={savedUser.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          savedUser.name?.[0] || '?'
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-white text-base font-semibold">Hi, {savedUser.name}</p>
                        <p className="text-white/40 text-xs">{savedUser.email}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password" className="text-sm text-white/70 font-medium">{t('password')}</Label>
                      <div className="relative">
                        <Input
                          data-testid="login-password-input"
                          id="password"
                          type={showPw ? 'text' : 'password'}
                          placeholder="Enter password"
                          name="inception-sport-password"
                          autoComplete="current-password"
                          autoFocus
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-amber-400/60 focus:ring-amber-400/30 h-11 text-sm pr-10 backdrop-blur-sm"
                        />
                        <button type="button" onClick={() => setShowPw(!showPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors">
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => { setUseSavedUser(false); setEmail(''); setPassword(''); }}
                        className="text-xs text-white/50 hover:text-white/70 transition-colors">
                        Use a different account
                      </button>
                      <button type="button" onClick={() => { setForgotEmail(savedUser.email); setMode('forgot'); }}
                        className="text-xs text-amber-400 hover:text-amber-300 font-medium transition-colors">
                        {t('forgot_password') || 'Forgot Password?'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm text-white/70 font-medium">{t('email')}</Label>
                      <Input
                        data-testid="login-email-input"
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        name="inception-sport-email"
                        autoComplete="username"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-amber-400/60 focus:ring-amber-400/30 h-11 text-sm backdrop-blur-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password" className="text-sm text-white/70 font-medium">{t('password')}</Label>
                      <div className="relative">
                        <Input
                          data-testid="login-password-input"
                          id="password"
                          type={showPw ? 'text' : 'password'}
                          placeholder="Enter password"
                          name="inception-sport-password"
                          autoComplete="current-password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-amber-400/60 focus:ring-amber-400/30 h-11 text-sm pr-10 backdrop-blur-sm"
                        />
                        <button type="button" onClick={() => setShowPw(!showPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors">
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer group" data-testid="remember-me-checkbox">
                        <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-white/30 bg-white/10 text-amber-400 focus:ring-amber-400/30 focus:ring-offset-0 cursor-pointer" />
                        <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors">Remember me</span>
                      </label>
                      <button type="button" onClick={() => { setForgotEmail(email); setMode('forgot'); }}
                        className="text-xs text-amber-400 hover:text-amber-300 font-medium transition-colors">
                        {t('forgot_password') || 'Forgot Password?'}
                      </button>
                    </div>
                  </>
                )}
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 text-sm font-semibold bg-amber-400 text-gray-900 hover:bg-amber-300 rounded-lg transition-all shadow-lg shadow-amber-400/20"
                >
                  {loading ? 'Signing in...' : t('login') || 'Sign In'}
                </Button>
              </form>

              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 border-t border-white/10" />
                <span className="text-xs text-white/20">or</span>
                <div className="flex-1 border-t border-white/10" />
              </div>

              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <Button
                data-testid="google-login-btn"
                type="button"
                variant="outline"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/auth/callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-11 text-sm font-medium bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-lg flex items-center justify-center gap-2.5 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Sign in with Google
              </Button>

              {isPasskeySupported() && (
                <Button
                  data-testid="passkey-login-btn"
                  type="button"
                  variant="outline"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  className="w-full h-11 text-sm font-medium bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-lg flex items-center justify-center gap-2.5 transition-all mt-2"
                >
                  <Fingerprint size={18} />
                  {passkeyLoading ? 'Verifying...' : 'Sign in with Passkey'}
                </Button>
              )}
            </>
          )}

          {mode === 'verify' && (
            <div className="space-y-6" data-testid="verify-code-form">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
                  <ShieldCheck size={24} className="text-amber-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">Verify your identity</h2>
                <p className="mt-2 text-white/50 text-sm">
                  We sent a 6-digit code to <strong className="text-white/80">{maskedEmail}</strong>
                </p>
              </div>

              <div className="flex justify-center gap-2" data-testid="code-inputs">
                {codeDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    data-testid={`code-digit-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={i === 0 ? 6 : 1}
                    value={digit}
                    onChange={e => handleCodeChange(i, e.target.value)}
                    onKeyDown={e => handleCodeKeyDown(i, e)}
                    className="w-11 h-12 text-center text-lg font-semibold border border-white/20 rounded-lg bg-white/10 text-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all backdrop-blur-sm"
                  />
                ))}
              </div>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-xs text-white/40">
                    Code expires in <span className="font-medium text-amber-400">{formatTime(countdown)}</span>
                  </p>
                ) : (
                  <p className="text-xs text-red-400 font-medium">Code expired</p>
                )}
              </div>

              <Button
                data-testid="verify-code-btn"
                onClick={() => handleVerify()}
                disabled={loading || codeDigits.some(d => !d)}
                className="w-full h-11 text-sm font-semibold bg-amber-400 text-gray-900 hover:bg-amber-300 rounded-lg shadow-lg shadow-amber-400/20"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>

              <div className="flex items-center justify-between">
                <button type="button" data-testid="resend-code-btn" onClick={handleResend} disabled={resending}
                  className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50 font-medium">
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
                <button type="button" onClick={() => { setMode('login'); setLoginToken(''); }}
                  className="flex items-center gap-1 text-xs text-white/50 hover:text-white/70">
                  <ArrowLeft size={12} /> Back to Login
                </button>
              </div>
            </div>
          )}

          {mode === 'forgot' && (
            <div className="space-y-5" data-testid="forgot-form">
              <div>
                <h2 className="text-xl font-semibold text-white">Forgot Password</h2>
                <p className="mt-1.5 text-white/50 text-sm">Enter your email and we'll send you a reset link</p>
              </div>
              <form onSubmit={handleForgot} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm text-white/70 font-medium">{t('email')}</Label>
                  <Input data-testid="forgot-email" type="email" placeholder="name@company.com"
                    value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-amber-400/60 h-11 text-sm" />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={loading}
                  className="w-full h-11 text-sm font-semibold bg-amber-400 text-gray-900 hover:bg-amber-300 rounded-lg shadow-lg shadow-amber-400/20">
                  {loading ? '...' : 'Send Reset Link'}
                </Button>
              </form>
              <button type="button" onClick={() => setMode('login')}
                className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white/70">
                <ArrowLeft size={14} /> Back to Login
              </button>
            </div>
          )}

          {mode === 'sent' && (
            <div className="text-center space-y-4" data-testid="forgot-sent">
              <Mail size={36} className="text-amber-400 mx-auto" />
              <h2 className="text-xl font-semibold text-white">Check your email</h2>
              <p className="text-white/50 text-sm">We've sent a password reset link to <strong className="text-white/80">{forgotEmail}</strong></p>
              <button type="button" onClick={() => setMode('login')}
                className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white/70 mt-4">
                <ArrowLeft size={14} /> Back to Login
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-white/25 text-[10px] mt-8">Powered by Inception</p>
        </div>
      </div>

      {/* Upcoming Matches Button */}
      <button
        data-testid="login-fixtures-btn"
        onClick={() => {
          if (!showFixtures) {
            api.get('/api/matches/upcoming/public').then(r => setFixtures(r.data)).catch(() => {});
          }
          setShowFixtures(!showFixtures);
        }}
        className="fixed bottom-6 left-6 z-20 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 backdrop-blur-lg border border-white/20 text-white text-sm font-medium hover:bg-white/25 transition-all shadow-lg"
      >
        <Calendar size={16} className="text-amber-400" />
        Upcoming Matches
      </button>

      {/* Fixtures Panel */}
      {showFixtures && (
        <div className="fixed bottom-20 left-6 z-20 w-96 max-h-[70vh] overflow-y-auto rounded-2xl bg-gray-900/95 backdrop-blur-xl border border-white/15 shadow-2xl animate-fade-in" data-testid="login-fixtures-panel">
          <div className="sticky top-0 bg-gray-900/95 backdrop-blur-xl px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-sm">Upcoming Matches</h3>
              {fixtures.round && <p className="text-white/50 text-xs mt-0.5">Round {fixtures.round}</p>}
            </div>
            <button onClick={() => setShowFixtures(false)} className="text-white/40 hover:text-white/80 transition-colors">
              <X size={16} />
            </button>
          </div>
          <div className="p-3 space-y-1">
            {fixtures.matches?.length > 0 ? fixtures.matches.map(m => (
 <div key={m.id} className="flex items-center gap-3 px-3 py-2.5 rounded-card shadow-card bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {m.home_team_badge && <img src={m.home_team_badge} alt="" className="w-5 h-5 object-contain" />}
                  <span className="text-white text-xs font-medium truncate">{m.home_team}</span>
                  <span className="text-white/30 text-[10px] shrink-0">vs</span>
                  {m.away_team_badge && <img src={m.away_team_badge} alt="" className="w-5 h-5 object-contain" />}
                  <span className="text-white text-xs font-medium truncate">{m.away_team}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white/60 text-[10px]">{m.match_date}</div>
                  {m.kickoff_time && <div className="text-amber-400 text-[10px] font-medium">{m.kickoff_time}</div>}
                </div>
              </div>
            )) : (
              <p className="text-white/40 text-sm text-center py-6">No upcoming matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
